import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { reportsApi, suppliersApi, whtApi, vatApi, ssclApi, payrollApi, fmtCents, type Supplier } from "../lib/business";
import { downloadFile } from "../lib/api";
import { useLangStore } from "../store/lang";
import { t } from "../i18n/strings";

export function ReportsPage() {
  const { lang } = useLangStore();
  const [tab, setTab] = useState<"pl" | "bs" | "ar" | "ap" | "tb" | "wht" | "vat" | "vatrecon" | "sscl" | "epf" | "etf" | "it">("pl");

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Reports</div>
          <h1 className="stage-title">
            Numbers, <em>made visual.</em>
          </h1>
          <p className="stage-sub">
            Charts above the fold. Statement structure below. Export and drill-down ship next.
          </p>
        </div>
      </div>

      <div className="bill-tabs" style={tabsStyle}>
        {[
          { k: "pl", l: "Profit & Loss" },
          { k: "bs", l: "Balance Sheet" },
          { k: "ar", l: "AR Ageing" },
          { k: "ap", l: "AP Ageing" },
          { k: "tb", l: "Trial Balance" },
          { k: "wht", l: "WHT Certificates" },
          { k: "vat", l: "VAT Return" },
          { k: "vatrecon", l: t("reports.tab.vatRecon", lang) },
          { k: "sscl", l: "SSCL Return" },
          { k: "epf", l: "EPF Form C" },
          { k: "etf", l: "ETF Form II" },
          { k: "it", l: "Income Tax" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k as typeof tab)}
            style={{
              padding: "7px 16px",
              borderRadius: 100,
              fontSize: 13,
              fontWeight: 500,
              color: tab === t.k ? "var(--paper)" : "var(--ink-3)",
              background: tab === t.k ? "var(--ink)" : "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "pl" && <PLReport />}
      {tab === "bs" && <BSReport />}
      {tab === "ar" && <ARReport />}
      {tab === "ap" && <APReport />}
      {tab === "tb" && <TBReport />}
      {tab === "wht" && <WhtCertificateReport />}
      {tab === "vat" && <VatReturnReport />}
      {tab === "vatrecon" && <VatReconciliationReport />}
      {tab === "sscl" && <SsclReturnReport />}
      {tab === "epf" && <EpfFormCReport />}
      {tab === "etf" && <EtfFormIIReport />}
      {tab === "it" && <IncomeTaxReport />}
    </>
  );
}

function PLReport() {
  const [compare, setCompare] = useState<"none" | "year" | "prior">("none");
  const { data, isLoading } = useQuery({
    queryKey: ["pl", compare],
    queryFn: () => reportsApi.profitLoss(undefined, undefined, compare),
  });
  if (isLoading || !data) return <div style={{ color: "var(--ink-3)" }}>Loading…</div>;

  const rev = BigInt(data.revenue.total_cents);
  const exp = BigInt(data.expenses.total_cents);
  const net = BigInt(data.net_profit_cents);
  const margin = rev > 0n ? Number((net * 1000n) / rev) / 10 : 0;
  const cmp = data.compare;
  const showCompare = cmp != null;

  // Build a code→amount map for the prior period so we can align lines.
  const priorRevByCode = new Map((cmp?.revenue.lines ?? []).map((l) => [l.code, l.amount_cents]));
  const priorExpByCode = new Map((cmp?.expenses.lines ?? []).map((l) => [l.code, l.amount_cents]));
  const compareLabel = compare === "year" ? "Last year" : "Prior period";

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
        <HeroTile label="Revenue" value={fmtCents(rev)} />
        <HeroTile label="Expenses" value={fmtCents(exp)} />
        <HeroTile label={`Net P&L · ${margin.toFixed(1)}% margin`} value={fmtCents(net)} featured />
      </div>

      <CompareToggle
        value={compare}
        onChange={(v) => setCompare(v as typeof compare)}
        options={[
          { k: "none", l: "None" },
          { k: "year", l: "Last year" },
          { k: "prior", l: "Prior period" },
        ]}
      />

      <div className="card card-pad-lg">
        <div className="stage-eyebrow">Statement</div>
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500, marginBottom: 12 }}>
          {fmtPeriod(data.period.start, data.period.end)}
        </div>

        {showCompare && <ColumnHeader current="This period" compare={compareLabel} />}

        <Section heading="Revenue">
          {data.revenue.lines.map((l) => (
            <PLLine
              key={l.code}
              label={`${l.code}  ${l.name}`}
              amount={l.amount_cents}
              compare={showCompare ? priorRevByCode.get(l.code) ?? null : undefined}
            />
          ))}
          <PLLine
            label="Total revenue"
            amount={data.revenue.total_cents}
            compare={showCompare ? cmp!.revenue.total_cents : undefined}
            subtotal
          />
        </Section>

        <Section heading="Expenses">
          {data.expenses.lines.map((l) => (
            <PLLine
              key={l.code}
              label={`${l.code}  ${l.name}`}
              amount={l.amount_cents}
              compare={showCompare ? priorExpByCode.get(l.code) ?? null : undefined}
            />
          ))}
          <PLLine
            label="Total expenses"
            amount={data.expenses.total_cents}
            compare={showCompare ? cmp!.expenses.total_cents : undefined}
            subtotal
          />
        </Section>

        <PLLine
          label="Net profit / (loss)"
          amount={data.net_profit_cents}
          compare={showCompare ? cmp!.net_profit_cents : undefined}
          grand
        />
      </div>
    </>
  );
}

function BSReport() {
  const [compare, setCompare] = useState<"none" | "year" | "month">("none");
  const { data, isLoading } = useQuery({
    queryKey: ["bs", compare],
    queryFn: () => reportsApi.balanceSheet(undefined, compare),
  });
  if (isLoading || !data) return <div style={{ color: "var(--ink-3)" }}>Loading…</div>;
  const a = BigInt(data.assets.total_cents);
  const l = BigInt(data.liabilities.total_cents);
  const e = BigInt(data.equity.total_cents);
  const balanced = a === l + e;
  const cmp = data.compare;
  const showCompare = cmp != null;
  const priorAssetByCode = new Map((cmp?.assets.lines ?? []).map((x) => [x.code, x.balance_cents]));
  const priorLiabByCode = new Map((cmp?.liabilities.lines ?? []).map((x) => [x.code, x.balance_cents]));
  const priorEqByCode = new Map((cmp?.equity.lines ?? []).map((x) => [x.code, x.balance_cents]));
  const compareLabel = compare === "year" ? "Last year" : "Last month";

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
        <HeroTile label="Assets" value={fmtCents(a)} />
        <HeroTile label="Liabilities" value={fmtCents(l)} />
        <HeroTile label="Equity" value={fmtCents(e)} featured={balanced} />
      </div>
      {!balanced && (
        <div className="banner banner-error" style={{ marginBottom: 16 }}>
          A ≠ L + E — there's a posting error. Diff: {fmtCents((a - (l + e)).toString())}
        </div>
      )}

      <CompareToggle
        value={compare}
        onChange={(v) => setCompare(v as typeof compare)}
        options={[
          { k: "none", l: "None" },
          { k: "year", l: "Last year" },
          { k: "month", l: "Last month" },
        ]}
      />

      <div className="card card-pad-lg">
        {showCompare && <ColumnHeader current="This date" compare={compareLabel} />}

        <Section heading="Assets">
          {data.assets.lines.map((line) => (
            <PLLine
              key={line.code}
              label={`${line.code}  ${line.name}`}
              amount={line.balance_cents}
              compare={showCompare ? priorAssetByCode.get(line.code) ?? null : undefined}
            />
          ))}
          <PLLine label="Total assets" amount={data.assets.total_cents} compare={showCompare ? cmp!.assets.total_cents : undefined} subtotal />
        </Section>

        <Section heading="Liabilities">
          {data.liabilities.lines.map((line) => (
            <PLLine
              key={line.code}
              label={`${line.code}  ${line.name}`}
              amount={line.balance_cents}
              compare={showCompare ? priorLiabByCode.get(line.code) ?? null : undefined}
            />
          ))}
          <PLLine label="Total liabilities" amount={data.liabilities.total_cents} compare={showCompare ? cmp!.liabilities.total_cents : undefined} subtotal />
        </Section>

        <Section heading="Equity">
          {data.equity.lines.map((line, i) => (
            <PLLine
              key={i}
              label={`${line.code}  ${line.name}`}
              amount={line.balance_cents}
              compare={showCompare ? priorEqByCode.get(line.code) ?? null : undefined}
            />
          ))}
          <PLLine label="Total equity" amount={data.equity.total_cents} compare={showCompare ? cmp!.equity.total_cents : undefined} subtotal />
        </Section>
      </div>
    </>
  );
}

function ARReport() {
  const { data, isLoading } = useQuery({ queryKey: ["ar"], queryFn: () => reportsApi.arAgeing() });
  if (isLoading || !data) return <div style={{ color: "var(--ink-3)" }}>Loading…</div>;
  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <a
          className="btn btn-line"
          href="/clearbooks/api/reports/ar-ageing.csv"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 12 }}
        >
          Download CSV
        </a>
      </div>
      <AgeingReport data={data} entityLabel="Customer" listKey="invoices" />
    </>
  );
}

function APReport() {
  const { data, isLoading } = useQuery({ queryKey: ["ap"], queryFn: () => reportsApi.apAgeing() });
  if (isLoading || !data) return <div style={{ color: "var(--ink-3)" }}>Loading…</div>;
  return <AgeingReport data={data} entityLabel="Supplier" listKey="bills" />;
}

interface AgeingPayload {
  buckets: {
    current_cents: string;
    days_1_30_cents: string;
    days_31_60_cents: string;
    days_61_90_cents: string;
    over_90_cents: string;
  };
  total_cents: string;
  invoices?: Array<{ id: string; invoice_number: string; customer: { name: string }; due_date: string; days_overdue: number; remaining_cents: string; bucket: string }>;
  bills?: Array<{ id: string; bill_number: string; supplier: { name: string }; due_date: string; days_overdue: number; remaining_cents: string; bucket: string }>;
}

function AgeingReport({ data, entityLabel, listKey }: { data: AgeingPayload; entityLabel: string; listKey: "invoices" | "bills" }) {
  const buckets = [
    { k: "current", l: "Current", v: data.buckets.current_cents, color: "var(--accent)" },
    { k: "1_30", l: "1–30 days", v: data.buckets.days_1_30_cents, color: "var(--accent)" },
    { k: "31_60", l: "31–60 days", v: data.buckets.days_31_60_cents, color: "var(--gold)" },
    { k: "61_90", l: "61–90 days", v: data.buckets.days_61_90_cents, color: "var(--gold)" },
    { k: "over_90", l: "Over 90", v: data.buckets.over_90_cents, color: "var(--rose)" },
  ];
  const total = BigInt(data.total_cents);
  const list = (listKey === "invoices" ? data.invoices : data.bills) ?? [];

  return (
    <>
      <div className="card card-pad-lg" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 500 }}>
            {entityLabel === "Customer" ? "Receivables" : "Payables"}
          </div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 500 }}>
            {fmtCents(total)}
          </div>
        </div>
        {buckets.map((b) => {
          const v = BigInt(b.v);
          const pct = total > 0n ? Number((v * 100n) / (total === 0n ? 1n : total)) : 0;
          return (
            <div key={b.k} style={{ display: "grid", gridTemplateColumns: "100px 1fr 130px", gap: 12, alignItems: "center", padding: "8px 0", fontSize: 13 }}>
              <span style={{ color: "var(--ink-3)", fontSize: 12 }}>{b.l}</span>
              <div style={{ background: "var(--paper-2)", height: 10, borderRadius: 100, overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${Math.min(100, pct)}%`,
                    background: b.color,
                    borderRadius: 100,
                    transition: "width 400ms ease",
                  }}
                />
              </div>
              <span style={{ textAlign: "right", fontWeight: 500 }} className="num">
                {fmtCents(v)}
              </span>
            </div>
          );
        })}
      </div>

      {list.length > 0 && (
        <div className="card card-pad-lg" style={{ padding: 0 }}>
          <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Number</th>
                <th style={th}>{entityLabel}</th>
                <th style={th}>Due</th>
                <th style={{ ...th, textAlign: "right" }}>Days overdue</th>
                <th style={{ ...th, textAlign: "right" }}>Remaining</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.id} style={{ borderBottom: "0.5px solid var(--line)" }}>
                  <td style={td}>{"invoice_number" in row ? row.invoice_number : (row as { bill_number: string }).bill_number}</td>
                  <td style={td}>{"customer" in row ? row.customer.name : (row as { supplier: { name: string } }).supplier.name}</td>
                  <td style={td}>{new Date(row.due_date).toLocaleDateString("en-LK", { day: "2-digit", month: "short" })}</td>
                  <td style={{ ...td, textAlign: "right" }} className="num">{row.days_overdue}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 500 }} className="num">{fmtCents(row.remaining_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </>
  );
}

function TBReport() {
  const { data, isLoading } = useQuery({ queryKey: ["tb"], queryFn: () => reportsApi.trialBalance() });
  if (isLoading || !data) return <div style={{ color: "var(--ink-3)" }}>Loading…</div>;
  const balanced = data.totals.debit_cents === data.totals.credit_cents;
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
        <HeroTile label="Total debits" value={fmtCents(data.totals.debit_cents)} />
        <HeroTile label="Total credits" value={fmtCents(data.totals.credit_cents)} />
        <HeroTile label={balanced ? "Balanced ✓" : "OUT OF BALANCE"} value={balanced ? "OK" : "ERROR"} featured={balanced} />
      </div>
      <div className="card card-pad-lg" style={{ padding: 0 }}>
        <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Code</th>
              <th style={th}>Account</th>
              <th style={th}>Type</th>
              <th style={{ ...th, textAlign: "right" }}>Debit</th>
              <th style={{ ...th, textAlign: "right" }}>Credit</th>
              <th style={{ ...th, textAlign: "right" }}>Balance</th>
            </tr>
          </thead>
          <tbody>
            {data.rows
              .filter((r) => BigInt(r.balance_cents) !== 0n)
              .map((r) => (
                <tr key={r.code} style={{ borderBottom: "0.5px solid var(--line)" }}>
                  <td style={td} className="num">{r.code}</td>
                  <td style={td}>{r.name}</td>
                  <td style={{ ...td, color: "var(--ink-4)", fontSize: 11, textTransform: "uppercase" }}>{r.type}</td>
                  <td style={{ ...td, textAlign: "right" }} className="num">{fmtCents(r.debit_cents)}</td>
                  <td style={{ ...td, textAlign: "right" }} className="num">{fmtCents(r.credit_cents)}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 500 }} className="num">{fmtCents(r.balance_cents)}</td>
                </tr>
              ))}
          </tbody>
        </table></div>
      </div>
    </>
  );
}

// ─── Common ───────────────────────────────────────────────────────────
function HeroTile({ label, value, featured }: { label: string; value: string; featured?: boolean }) {
  return (
    <div className={`hero-tile ${featured ? "featured" : ""}`}>
      <div className="hero-label">{label}</div>
      <div className="hero-num" style={{ fontSize: 28 }}>{value}</div>
    </div>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div
        style={{
          fontFamily: "'Fraunces', serif",
          fontWeight: 500,
          fontSize: 16,
          color: "var(--ink-2)",
          paddingBottom: 8,
          borderBottom: "0.5px solid var(--line)",
          marginBottom: 8,
        }}
      >
        {heading}
      </div>
      {children}
    </div>
  );
}

function PLLine({
  label,
  amount,
  compare,
  subtotal,
  grand,
}: {
  label: string;
  amount: string | bigint;
  compare?: string | bigint | null;
  subtotal?: boolean;
  grand?: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: compare !== undefined ? "1fr 140px 140px" : "1fr 140px",
        gap: 16,
        padding: "8px 0",
        fontSize: grand ? 16 : 14,
        fontWeight: subtotal || grand ? 500 : 400,
        fontFamily: grand ? "'Fraunces', serif" : "inherit",
        borderTop: subtotal ? "0.5px solid var(--line)" : grand ? "1px solid var(--ink)" : undefined,
        marginTop: subtotal ? 6 : grand ? 14 : 0,
        paddingTop: subtotal ? 12 : grand ? 14 : 8,
        color: grand ? "var(--ink)" : "var(--ink-2)",
      }}
    >
      <span>{label}</span>
      <span style={{ textAlign: "right" }} className="num">{fmtCents(amount)}</span>
      {compare !== undefined && (
        <span style={{ textAlign: "right", color: "var(--ink-3)" }} className="num">
          {compare == null ? "—" : fmtCents(compare)}
        </span>
      )}
    </div>
  );
}

function CompareToggle({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ k: string; l: string }>;
}) {
  return (
    <div style={{ display: "flex", gap: 4, padding: 4, background: "var(--paper-2)", borderRadius: 100, border: "0.5px solid var(--line)", width: "fit-content", marginBottom: 16 }}>
      <span style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-4)", padding: "7px 10px" }}>Compare</span>
      {options.map((o) => (
        <button
          key={o.k}
          type="button"
          onClick={() => onChange(o.k)}
          style={{
            padding: "7px 14px",
            borderRadius: 100,
            fontSize: 12,
            fontWeight: 500,
            color: value === o.k ? "var(--paper)" : "var(--ink-3)",
            background: value === o.k ? "var(--ink)" : "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function ColumnHeader({ current, compare }: { current: string; compare?: string }) {
  if (!compare) return null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 140px 140px",
        gap: 16,
        padding: "0 0 6px",
        fontSize: 11,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "var(--ink-4)",
        fontWeight: 500,
      }}
    >
      <span />
      <span style={{ textAlign: "right" }}>{current}</span>
      <span style={{ textAlign: "right" }}>{compare}</span>
    </div>
  );
}

function fmtPeriod(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const f = (d: Date) => d.toLocaleDateString("en-LK", { day: "2-digit", month: "short", year: "numeric" });
  return `${f(s)} → ${f(e)}`;
}

const tabsStyle: React.CSSProperties = {
  display: "flex",
  gap: 4,
  padding: 4,
  background: "var(--paper-2)",
  borderRadius: 100,
  border: "0.5px solid var(--line)",
  marginBottom: 20,
  width: "fit-content",
};
const th: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 18px",
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--ink-4)",
  fontWeight: 500,
  background: "var(--paper)",
  borderBottom: "0.5px solid var(--line)",
};
const td: React.CSSProperties = {
  padding: "14px 18px",
  fontSize: 13,
  borderBottom: "0.5px solid var(--line)",
};

// ─── WHT Certificate ──────────────────────────────────────────────────────
function WhtCertificateReport() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [supplierId, setSupplierId] = useState<string>("");

  const { data: suppliersData } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => suppliersApi.list(),
  });
  const suppliers: Supplier[] = suppliersData?.suppliers ?? [];

  const { data: cert, isLoading } = useQuery({
    queryKey: ["wht-cert", supplierId, year],
    queryFn: () => whtApi.certificate(supplierId, year),
    enabled: !!supplierId,
  });

  const monthName = (m: number) =>
    new Date(2000, m - 1, 1).toLocaleDateString("en-LK", { month: "long" });

  return (
    <>
      <div className="card card-pad-lg" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 12, alignItems: "end" }}>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Supplier</label>
            <select
              className="form-input"
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
            >
              <option value="">— Pick a supplier —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Year</label>
            <select className="form-input" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <button
            className="btn btn-line"
            onClick={() => window.print()}
            disabled={!cert || cert.totals.wht_cents === "0"}
          >
            Print
          </button>
        </div>
      </div>

      {!supplierId ? (
        <div className="card card-pad-lg" style={{ color: "var(--ink-3)", textAlign: "center" }}>
          Pick a supplier to generate their certificate.
        </div>
      ) : isLoading || !cert ? (
        <div className="card card-pad-lg" style={{ color: "var(--ink-3)" }}>Loading…</div>
      ) : cert.totals.wht_cents === "0" ? (
        <div className="card card-pad-lg" style={{ color: "var(--ink-3)", textAlign: "center" }}>
          No WHT was withheld from {cert.supplier.name} during {year}.
        </div>
      ) : (
        <div className="card card-pad-lg">
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 500, marginBottom: 6 }}>
            Certificate of Tax Withheld
          </div>
          <div style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 18 }}>
            Year of Assessment {year} · Inland Revenue Department, Sri Lanka
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
            <div>
              <div className="stage-eyebrow">Withholdee</div>
              <div style={{ fontWeight: 500, marginTop: 4 }}>{cert.supplier.name}</div>
              {cert.supplier.address && <div style={{ fontSize: 13, color: "var(--ink-3)" }}>{cert.supplier.address}</div>}
              {cert.supplier.tin && <div style={{ fontSize: 13 }}>TIN: <strong>{cert.supplier.tin}</strong></div>}
              {cert.supplier.nic_or_passport && <div style={{ fontSize: 13 }}>NIC/Passport: <strong>{cert.supplier.nic_or_passport}</strong></div>}
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>
                {cert.supplier.is_resident ? "Resident" : "Non-resident"} · {cert.supplier.is_individual ? "Individual" : "Entity"}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="stage-eyebrow">Total withheld</div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 28, fontWeight: 500, marginTop: 4 }}>
                {fmtCents(cert.totals.wht_cents)}
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                on gross {fmtCents(cert.totals.gross_cents)}
              </div>
            </div>
          </div>

          <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Month</th>
                <th style={{ ...th, textAlign: "right" }}>Gross</th>
                <th style={{ ...th, textAlign: "right" }}>WHT</th>
                <th style={th}>Bills</th>
              </tr>
            </thead>
            <tbody>
              {cert.months.map((m) => (
                <tr key={m.month}>
                  <td style={td}>{monthName(m.month)}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(m.gross_cents)}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500 }}>{fmtCents(m.wht_cents)}</td>
                  <td style={{ ...td, fontSize: 12, color: "var(--ink-3)" }}>
                    {m.bills.map((b) => b.bill_number).join(", ")}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ ...td, fontWeight: 500, borderTop: "1px solid var(--ink)" }}>Total</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500, borderTop: "1px solid var(--ink)" }}>
                  {fmtCents(cert.totals.gross_cents)}
                </td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500, borderTop: "1px solid var(--ink)" }}>
                  {fmtCents(cert.totals.wht_cents)}
                </td>
                <td style={{ ...td, borderTop: "1px solid var(--ink)" }} />
              </tr>
            </tbody>
          </table></div>
        </div>
      )}
    </>
  );
}

// ─── VAT Return ───────────────────────────────────────────────────────────
function VatReturnReport() {
  const today = new Date();
  // Default: current quarter
  const q = Math.floor(today.getMonth() / 3);
  const defaultStart = new Date(today.getFullYear(), q * 3, 1);
  const defaultEnd = new Date(today.getFullYear(), q * 3 + 3, 0);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  const [start, setStart] = useState(fmt(defaultStart));
  const [end, setEnd] = useState(fmt(defaultEnd));

  const { data, isLoading } = useQuery({
    queryKey: ["vat-return", start, end],
    queryFn: () => vatApi.getReturn(start, end),
  });

  const downloadCsv = () => {
    if (!data) return;
    const lines = ["Cage,Label,Value (LKR)"];
    for (const c of data.cages) {
      const v = (Number(c.value_cents) / 100).toFixed(2);
      lines.push(`${c.code},"${c.label.replace(/"/g, '""')}",${v}`);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vat-return-${start}-to-${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="card card-pad-lg" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto auto", gap: 12, alignItems: "end" }}>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Period start</label>
            <input className="form-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Period end</label>
            <input className="form-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <button className="btn btn-line" onClick={downloadCsv} disabled={!data}>Export CSV</button>
          <button className="btn btn-line" onClick={() => downloadFile(`/vat/return?period_start=${start}&period_end=${end}&format=xlsx`, `vat-return-${start}-to-${end}.xlsx`)} disabled={!data}>Export Excel</button>
          <button className="btn btn-line" onClick={() => window.print()} disabled={!data}>Print</button>
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--ink-3)" }}>
          Aggregates posted invoices and bills for the window. Lines without a VAT classification default to <strong>Taxable</strong>.
          Set classifications on individual invoice/bill lines to populate the zero-rated, exempt, deemed, and import cages.
        </div>
      </div>

      {isLoading || !data ? (
        <div className="card card-pad-lg" style={{ color: "var(--ink-3)" }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
            <HeroTile label="Total output tax" value={fmtCents(data.summary.total_output_tax_cents)} />
            <HeroTile label="Total input tax" value={fmtCents(data.summary.total_input_tax_cents)} />
            <HeroTile
              label={Number(data.summary.net_payable_cents) >= 0 ? "Net payable" : "Refund due"}
              value={fmtCents(Math.abs(Number(data.summary.net_payable_cents)).toString())}
              featured
            />
          </div>

          <div className="card card-pad-lg">
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500, marginBottom: 12 }}>
              VAT Return — {fmtPeriod(start, end)}
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-3)", marginBottom: 14 }}>
              Form Asmt_VAT_001_E · Inland Revenue Department · standard rate {data.vat_rate_pct}%
            </div>
            <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Cage</th>
                  <th style={th}>Description</th>
                  <th style={{ ...th, textAlign: "right" }}>Value (LKR)</th>
                </tr>
              </thead>
              <tbody>
                {data.cages.map((c) => (
                  <tr key={c.code}>
                    <td style={{ ...td, fontFamily: "var(--font-mono, monospace)", fontWeight: 500, width: 60 }}>{c.code}</td>
                    <td style={td}>{c.label}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>
                      {fmtCents(c.value_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>

          <div className="card card-pad-lg" style={{ marginTop: 16 }}>
            <div className="stage-eyebrow">Schedules</div>
            <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 4, marginBottom: 12 }}>
              Per-document detail required by the IRD e-services upload.
            </div>
            <ScheduleSummary label="Sch 01 — Output (taxable invoices)" rows={data.schedules.sch_01_output} />
            <ScheduleSummary label="Sch 02 — Zero-rated supplies" rows={data.schedules.sch_02_zero_rated} />
            <ScheduleSummary label="Sch 03 — Exempt supplies" rows={data.schedules.sch_03_exempt} />
            <ScheduleSummary label="Sch 04 — Deemed taxable supplies" rows={data.schedules.sch_04_deemed} />
            <ScheduleSummary label="Sch 05 — Imports" rows={data.schedules.sch_05_imports} />
            <ScheduleSummary label="Sch 06 — Local purchases (input claim)" rows={data.schedules.sch_06_local_purchases} />
            <ScheduleSummary label="Sch 07 — Non-VAT vendor purchases" rows={data.schedules.sch_07_non_vat} />
          </div>
        </>
      )}
    </>
  );
}

function ScheduleSummary({ label, rows }: { label: string; rows: Array<Record<string, unknown>> }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "0.5px solid var(--line)" }}>
      <span style={{ fontSize: 13 }}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--ink-3)" }}>{rows.length} {rows.length === 1 ? "row" : "rows"}</span>
    </div>
  );
}

// ─── VAT Reconciliation ─────────────────────────────────────────────────
// Places each VAT ledger control account next to its return cage and breaks
// out the difference, so a clean return that fails to tie to the ledger is
// caught here by the accountant rather than later by an auditor.
function VatReconciliationReport() {
  const { lang } = useLangStore();
  const today = new Date();
  const q = Math.floor(today.getMonth() / 3);
  const defaultStart = new Date(today.getFullYear(), q * 3, 1);
  const defaultEnd = new Date(today.getFullYear(), q * 3 + 3, 0);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  const [start, setStart] = useState(fmt(defaultStart));
  const [end, setEnd] = useState(fmt(defaultEnd));

  const { data, isLoading } = useQuery({
    queryKey: ["vat-recon", start, end],
    queryFn: () => vatApi.reconciliation(start, end),
  });

  return (
    <>
      <div className="card card-pad-lg" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Period start</label>
            <input className="form-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Period end</label>
            <input className="form-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <button className="btn btn-line" onClick={() => window.print()} disabled={!data}>Print</button>
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--ink-3)" }}>
          Compares the VAT figures on the return against the VAT control accounts in the ledger.
          A clean set of books reconciles to zero on every row. Any non-zero difference points to a
          posting or classification issue to investigate before filing.
        </div>
      </div>

      {isLoading || !data ? (
        <div className="card card-pad-lg" style={{ color: "var(--ink-3)" }}>Loading…</div>
      ) : (
        <>
          <div
            className="banner"
            style={{
              marginBottom: 16,
              background: data.reconciled ? "var(--ok-bg, #e7f6ec)" : "var(--err-bg, #fdeaea)",
              color: data.reconciled ? "var(--ok-ink, #1b5e3a)" : "var(--err-ink, #8a1c1c)",
              fontWeight: 500,
            }}
          >
            {data.reconciled
              ? `✓ ${t("vatRecon.reconciled", lang)}`
              : `✗ ${t("vatRecon.notReconciled", lang)}`}
          </div>

          <div className="card card-pad-lg">
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500, marginBottom: 12 }}>
              {t("vatRecon.title", lang)} — {fmtPeriod(start, end)}
            </div>
            <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>{t("vatRecon.col.account", lang)}</th>
                  <th style={{ ...th, width: 70 }}>{t("vatRecon.col.cage", lang)}</th>
                  <th style={{ ...th, textAlign: "right" }}>{t("vatRecon.col.return", lang)}</th>
                  <th style={{ ...th, textAlign: "right" }}>{t("vatRecon.col.ledger", lang)}</th>
                  <th style={{ ...th, textAlign: "right" }}>{t("vatRecon.col.difference", lang)}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.label} style={r.reconciled ? undefined : { background: "var(--err-bg, #fdeaea)" }}>
                    <td style={td}>{r.label}</td>
                    <td style={{ ...td, fontFamily: "var(--font-mono, monospace)" }}>{r.cage ?? "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.return_cents)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.ledger_cents)}</td>
                    <td
                      style={{
                        ...td,
                        textAlign: "right",
                        fontFamily: "var(--font-mono, monospace)",
                        fontWeight: r.reconciled ? 400 : 600,
                        color: r.reconciled ? "var(--ink-3)" : "var(--err-ink, #8a1c1c)",
                      }}
                    >
                      {fmtCents(r.difference_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        </>
      )}
    </>
  );
}

// ─── SSCL Return ──────────────────────────────────────────────────────────
function SsclReturnReport() {
  const today = new Date();
  const q = Math.floor(today.getMonth() / 3);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const [start, setStart] = useState(fmt(new Date(today.getFullYear(), q * 3, 1)));
  const [end, setEnd] = useState(fmt(new Date(today.getFullYear(), q * 3 + 3, 0)));

  const { data, isLoading } = useQuery({
    queryKey: ["sscl-return", start, end],
    queryFn: () => ssclApi.getReturn(start, end),
  });

  return (
    <>
      <div className="card card-pad-lg" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Quarter start</label>
            <input className="form-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Quarter end</label>
            <input className="form-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <button className="btn btn-line" onClick={() => window.print()} disabled={!data}>Print</button>
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--ink-3)" }}>
          SSCL_20 quarterly return per SSCL Act §8 · 2.5% on liable turnover · sector multiplier auto-applied
          {data && ` · default sector: ${data.tenant_default_sector.replace("_", " ")}`}.
        </div>
      </div>

      {isLoading || !data ? (
        <div className="card card-pad-lg" style={{ color: "var(--ink-3)" }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
            <HeroTile label="Total turnover" value={fmtCents(data.summary.total_gross_cents)} />
            <HeroTile label="SSCL @ 2.5%" value={fmtCents(data.summary.total_sscl_cents)} featured />
            <HeroTile label={`Installments (N1/N2/N3)`} value={`${data.installments.length}`} />
          </div>

          <div className="card card-pad-lg">
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500, marginBottom: 12 }}>
              SSCL_20 — Quarterly summary
            </div>
            <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Month</th>
                  <th style={{ ...th, textAlign: "right" }}>Gross turnover</th>
                  <th style={{ ...th, textAlign: "right" }}>Liable</th>
                  <th style={{ ...th, textAlign: "right" }}>SSCL @ 2.5%</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((m) => {
                  const liableTotal = Object.values(m.liable_by_sector).reduce(
                    (s, v) => s + BigInt(v), 0n,
                  );
                  return (
                    <tr key={m.month_index}>
                      <td style={td}>{m.label}</td>
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(m.gross_total_cents)}</td>
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(liableTotal.toString())}</td>
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500 }}>{fmtCents(m.sscl_payable_cents)}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td style={{ ...td, fontWeight: 500, borderTop: "1px solid var(--ink)" }}>Total</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500, borderTop: "1px solid var(--ink)" }}>
                    {fmtCents(data.summary.total_gross_cents)}
                  </td>
                  <td style={{ ...td, borderTop: "1px solid var(--ink)" }} />
                  <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500, borderTop: "1px solid var(--ink)" }}>
                    {fmtCents(data.summary.total_sscl_cents)}
                  </td>
                </tr>
              </tbody>
            </table></div>
          </div>
        </>
      )}
    </>
  );
}

// ─── EPF Form C ──────────────────────────────────────────────────────────
function EpfFormCReport() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const { data, isLoading } = useQuery({
    queryKey: ["epf-form-c", year, month],
    queryFn: () => payrollApi.epfFormC(year, month),
  });

  const monthName = (m: number) =>
    new Date(2000, m - 1, 1).toLocaleDateString("en-LK", { month: "long" });

  return (
    <>
      <div className="card card-pad-lg" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Year</label>
            <select className="form-input" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[year + 1, year, year - 1, year - 2].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Month</label>
            <select className="form-input" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
            </select>
          </div>
          <button className="btn btn-line" onClick={() => downloadFile(`/payroll/epf/form-c?year=${year}&month=${month}&format=xlsx`, `epf-form-c-${year}-${String(month).padStart(2,"0")}.xlsx`)} disabled={!data}>Export Excel</button>
          <button className="btn btn-line" onClick={() => window.print()} disabled={!data}>Print</button>
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--ink-3)" }}>
          EPF Form C (M-13) · monthly remittance · 8% employee + 12% employer = 20%
        </div>
      </div>

      {isLoading || !data ? (
        <div className="card card-pad-lg" style={{ color: "var(--ink-3)" }}>Loading…</div>
      ) : data.summary.member_count === 0 ? (
        <div className="card card-pad-lg" style={{ color: "var(--ink-3)", textAlign: "center" }}>
          No payroll entries for {monthName(month)} {year}. Add some via the Payroll module (coming soon).
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
            <HeroTile label="Members" value={String(data.summary.member_count)} />
            <HeroTile label="Total earnings" value={fmtCents(data.summary.total_earnings_cents)} />
            <HeroTile label="Total contribution" value={fmtCents(data.summary.total_remittance_cents)} featured />
            <HeroTile label="Period" value={`${monthName(month)} ${year}`} />
          </div>

          <div className="card card-pad-lg">
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--ink-4)" }}>To</div>
              <div style={{ fontWeight: 500 }}>The Superintendent, Employees' Provident Fund</div>
              <div style={{ fontSize: 13, color: "var(--ink-3)" }}>Central Bank of Sri Lanka, P.O. Box 1299, Colombo 1</div>
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>Cheque payable to: <strong>{data.payee}</strong></div>
            </div>
            <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Employee</th>
                  <th style={th}>NIC</th>
                  <th style={th}>Member #</th>
                  <th style={{ ...th, textAlign: "right" }}>Earnings</th>
                  <th style={{ ...th, textAlign: "right" }}>Employer 12%</th>
                  <th style={{ ...th, textAlign: "right" }}>Employee 8%</th>
                  <th style={{ ...th, textAlign: "right" }}>Total 20%</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i}>
                    <td style={td}>{r.employee_name}</td>
                    <td style={{ ...td, fontSize: 12, color: "var(--ink-3)" }}>{r.nic ?? "—"}</td>
                    <td style={{ ...td, fontSize: 12, color: "var(--ink-3)", fontFamily: "var(--font-mono, monospace)" }}>{r.member_no ?? "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.total_earnings_cents)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.employer_share_cents)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.employee_share_cents)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500 }}>{fmtCents(r.total_contribution_cents)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} style={{ ...td, fontWeight: 500, borderTop: "1px solid var(--ink)" }}>Total remittance</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500, borderTop: "1px solid var(--ink)" }}>{fmtCents(data.summary.total_earnings_cents)}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500, borderTop: "1px solid var(--ink)" }}>{fmtCents(data.summary.employer_share_cents)}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500, borderTop: "1px solid var(--ink)" }}>{fmtCents(data.summary.employee_share_cents)}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500, borderTop: "1px solid var(--ink)" }}>{fmtCents(data.summary.total_remittance_cents)}</td>
                </tr>
              </tbody>
            </table></div>
          </div>
        </>
      )}
    </>
  );
}

// ─── ETF Form II ──────────────────────────────────────────────────────────
function EtfFormIIReport() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [half, setHalf] = useState<1 | 2>(today.getMonth() < 6 ? 1 : 2);

  const { data, isLoading } = useQuery({
    queryKey: ["etf-form-ii", year, half],
    queryFn: () => payrollApi.etfFormII(year, half),
  });

  const monthName = (m: number) =>
    new Date(2000, m - 1, 1).toLocaleDateString("en-LK", { month: "short" });

  return (
    <>
      <div className="card card-pad-lg" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Year</label>
            <select className="form-input" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[year + 1, year, year - 1, year - 2].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Half</label>
            <select className="form-input" value={half} onChange={(e) => setHalf(Number(e.target.value) as 1 | 2)}>
              <option value={1}>H1 (Jan – Jun)</option>
              <option value={2}>H2 (Jul – Dec)</option>
            </select>
          </div>
          <button className="btn btn-line" onClick={() => downloadFile(`/payroll/etf/form-ii?year=${year}&half=${half}&format=xlsx`, `etf-form-ii-${year}-H${half}.xlsx`)} disabled={!data}>Export Excel</button>
          <button className="btn btn-line" onClick={() => window.print()} disabled={!data}>Print</button>
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--ink-3)" }}>
          ETF Form II · half-yearly · 3% employer-only · due last working day of August (H1) / February (H2)
        </div>
      </div>

      {isLoading || !data ? (
        <div className="card card-pad-lg" style={{ color: "var(--ink-3)" }}>Loading…</div>
      ) : data.form_ii.member_count === 0 ? (
        <div className="card card-pad-lg" style={{ color: "var(--ink-3)", textAlign: "center" }}>
          No payroll entries for {year} H{half}.
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
            <HeroTile label="Members" value={String(data.form_ii.member_count)} />
            <HeroTile label="Total contribution" value={fmtCents(data.summary.grand_total_contributions_cents)} featured />
            <HeroTile
              label={data.summary.reconciles ? "Reconciles ✓" : "Mismatch"}
              value={fmtCents(data.summary.grand_total_remitted_cents)}
              featured={data.summary.reconciles}
            />
          </div>

          <div className="card card-pad-lg" style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Form II — member detail</div>
            <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={th}>Member name</th>
                  <th style={th}>Member #</th>
                  <th style={th}>NIC</th>
                  {data.header.months.map((m) => (
                    <th key={m} style={{ ...th, textAlign: "right" }}>{monthName(m)}</th>
                  ))}
                  <th style={{ ...th, textAlign: "right" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.form_ii.members.map((m, i) => (
                  <tr key={i}>
                    <td style={td}>{m.member_name}</td>
                    <td style={{ ...td, fontFamily: "var(--font-mono, monospace)" }}>{m.member_no ?? "—"}</td>
                    <td style={{ ...td, color: "var(--ink-3)" }}>{m.nic ?? "—"}</td>
                    {m.months.map((mm) => (
                      <td key={mm.month} style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(mm.contribution_cents)}</td>
                    ))}
                    <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500 }}>{fmtCents(m.total_contribution_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>

          <div className="card card-pad-lg">
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Reconciliation Statement</div>
            <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={th}>Month</th>
                  <th style={{ ...th, textAlign: "right" }}>Payable</th>
                  <th style={{ ...th, textAlign: "right" }}>Remitted</th>
                  <th style={th}>Cheque #</th>
                  <th style={th}>Bank</th>
                  <th style={{ ...th, textAlign: "right" }}>Variance</th>
                </tr>
              </thead>
              <tbody>
                {data.reconciliation.map((r) => (
                  <tr key={r.month}>
                    <td style={td}>{monthName(r.month)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.payable_cents)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.remitted_cents)}</td>
                    <td style={{ ...td, fontFamily: "var(--font-mono, monospace)", color: "var(--ink-3)" }}>{r.cheque_no ?? "—"}</td>
                    <td style={{ ...td, color: "var(--ink-3)" }}>{r.bank ?? "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.variance_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        </>
      )}
    </>
  );
}

// ─── Income Tax Computation ──────────────────────────────────────────────
function IncomeTaxReport() {
  const today = new Date();
  // Default: current FY (Sri Lanka FY = Apr → Mar)
  const fy = today.getMonth() < 3 ? today.getFullYear() - 1 : today.getFullYear();
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const [start, setStart] = useState(fmt(new Date(fy, 3, 1)));
  const [end, setEnd] = useState(fmt(new Date(fy + 1, 2, 31)));

  const { data, isLoading } = useQuery({
    queryKey: ["it-comp", start, end],
    queryFn: () => reportsApi.incomeTaxComputation(start, end),
  });

  return (
    <>
      <div className="card card-pad-lg" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Period start</label>
            <input className="form-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Period end</label>
            <input className="form-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <button className="btn btn-line" onClick={() => window.print()} disabled={!data}>Print</button>
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--ink-3)" }}>
          Adds disallowable lines back to net profit. Partial-allowance items (entertainment, donations) are
          flagged for manual review against the IRA caps before adding back.
        </div>
      </div>

      {isLoading || !data ? (
        <div className="card card-pad-lg" style={{ color: "var(--ink-3)" }}>Loading…</div>
      ) : (
        <>
          <div
            className="annotation"
            style={{
              marginBottom: 16,
              background: "#FFF4E5",
              borderColor: "#E8A53D",
              color: "#7A4500",
            }}
          >
            <span className="annot-label" style={{ color: "#7A4500" }}>Beta:</span>
            The income-tax computation is calculated from your ledger — verify with your
            accountant before submission. Online filing on RAMIS is not fully automated
            (it's a multi-step manual process even for accountants); this report is meant
            as a working schedule, not a final return.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
            <HeroTile label="Accounting net profit" value={fmtCents(data.net_profit_cents)} />
            <HeroTile label="Add-backs (disallowable)" value={fmtCents(data.add_backs.disallowable.total_cents)} />
            <HeroTile label="Adjusted profit" value={fmtCents(data.adjusted_profit_cents)} featured />
          </div>

          <div className="card card-pad-lg" style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500, marginBottom: 12 }}>
              Income tax computation
            </div>
            <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td style={td}><strong>Net profit per accounts</strong></td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500 }}>{fmtCents(data.net_profit_cents)}</td>
                </tr>
                {data.add_backs.disallowable.rows.length > 0 && (
                  <>
                    <tr>
                      <td style={{ ...td, paddingTop: 16 }} colSpan={2}>
                        <em style={{ color: "var(--ink-3)" }}>Add: disallowable expenses</em>
                      </td>
                    </tr>
                    {data.add_backs.disallowable.rows.map((r) => (
                      <tr key={r.code}>
                        <td style={{ ...td, paddingLeft: 24 }}>
                          <code style={{ fontSize: 11, marginRight: 8 }}>{r.code}</code>
                          {r.name}
                        </td>
                        <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.amount_cents)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...td, paddingLeft: 24, fontWeight: 500 }}>Subtotal</td>
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500 }}>{fmtCents(data.add_backs.disallowable.total_cents)}</td>
                    </tr>
                  </>
                )}
                <tr>
                  <td style={{ ...td, borderTop: "1px solid var(--ink)", paddingTop: 16, fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 500 }}>
                    Adjusted profit
                  </td>
                  <td style={{ ...td, borderTop: "1px solid var(--ink)", paddingTop: 16, textAlign: "right", fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 500 }}>
                    {fmtCents(data.adjusted_profit_cents)}
                  </td>
                </tr>
              </tbody>
            </table></div>
          </div>

          {data.add_backs.partial_review.rows.length > 0 && (
            <div className="card card-pad-lg">
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
                Manual-review (partial allowance)
              </div>
              <p style={{ fontSize: 13, color: "var(--ink-3)", marginBottom: 12 }}>{data.add_backs.partial_review.note}</p>
              <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Code</th>
                    <th style={th}>Account</th>
                    <th style={{ ...th, textAlign: "right" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.add_backs.partial_review.rows.map((r) => (
                    <tr key={r.code}>
                      <td style={td}><code style={{ fontSize: 11 }}>{r.code}</code></td>
                      <td style={td}>{r.name}</td>
                      <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.amount_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}
        </>
      )}
    </>
  );
}
